import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import DataObjectIcon from "@mui/icons-material/DataObject";
import ArticleIcon from "@mui/icons-material/Article";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/PageHeader";
import { useCandidate } from "@/context/CandidateContext";
import { useAuth } from "@/hooks/useAuth";
import { fetchProfile, profileQueryKey } from "@/lib/queries/profile";
import { buildBookmarkletHref, type BookmarkletProfile } from "@/lib/bookmarklet";
import BookmarkAddedIcon from "@mui/icons-material/BookmarkAdded";

export const Route = createFileRoute("/app/autofill")({
  beforeLoad: () => {
    throw redirect({ to: "/app/tools", search: { tab: "autofill" } });
  },
  component: () => null,
});

type AtsKey = "workday" | "greenhouse" | "lever" | "ashby";

type FieldDef = {
  label: string;
  ats: Partial<Record<AtsKey, string[]>>;
  value: string;
  multiline?: boolean;
};

function ynLabel(v: boolean | null | undefined): string {
  if (v === true) return "Yes";
  if (v === false) return "No";
  return "";
}

export function AutofillPanel({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const { profile } = useCandidate();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [tab, setTab] = useState<AtsKey>("workday");
  const [copied, setCopied] = useState<string | null>(null);

  const { data: profileRow } = useQuery({
    queryKey: userId ? profileQueryKey(userId) : ["profile", "anon"],
    queryFn: () => fetchProfile(userId as string),
    enabled: !!userId,
  });

  const summary = profile.resume.summary ?? "";
  const topSkills = profile.verifiedSkills.slice(0, 12).map((s) => s.name);
  const topLanguages = profile.github.topLanguages.slice(0, 6).map((l) => l.name);
  const latestRole = profile.resume.chronologicalExperience[0];

  // Source of truth: profiles row first, then candidate sources as fallback.
  const firstName = profileRow?.first_name ?? "";
  const lastName = profileRow?.last_name ?? "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const phone = profileRow?.phone ?? "";
  const pronouns = profileRow?.pronouns ?? "";
  const location = profileRow?.location ?? profile.linkedin.location ?? "";
  const headline = profileRow?.headline ?? profile.linkedin.headline ?? "";
  const linkedinUrl = profileRow?.linkedin_url ?? profile.linkedin.profileUrl ?? "";
  const githubUrl =
    profileRow?.github_url ??
    (profile.github.username ? `https://github.com/${profile.github.username}` : "");
  const portfolioUrl = profileRow?.portfolio_url ?? profile.portfolio.links[0] ?? "";
  const workAuth = profileRow?.work_authorization ?? "";
  const sponsorship = ynLabel(profileRow?.requires_sponsorship);
  const gender = profileRow?.gender ?? "";
  const race = profileRow?.race_ethnicity ?? "";
  const hispanic = ynLabel(profileRow?.hispanic_latino);
  const veteran = profileRow?.veteran_status ?? "";
  const disability = profileRow?.disability_status ?? "";
  const orientation = profileRow?.sexual_orientation ?? "";

  const workHistoryBlock = useMemo(() => {
    if (!profile.resume.chronologicalExperience.length) return "";
    return profile.resume.chronologicalExperience
      .map((exp) => {
        const dates = `${exp.start}${exp.end ? ` – ${exp.end}` : " – Present"}`;
        const lines = [
          `${exp.role} — ${exp.company} (${dates})`,
          exp.description ?? "",
          ...(exp.highlights ?? []).map((h) => `• ${h}`),
        ].filter(Boolean);
        return lines.join("\n");
      })
      .join("\n\n");
  }, [profile.resume.chronologicalExperience]);

  const educationBlock = useMemo(() => {
    if (!profile.resume.education.length) return "";
    return profile.resume.education
      .map((e) => {
        const dates = `${e.start}${e.end ? ` – ${e.end}` : " – Present"}`;
        return `${e.degree} — ${e.school} (${dates})`;
      })
      .join("\n");
  }, [profile.resume.education]);

  const fields: FieldDef[] = [
    { label: "First name", ats: { workday: ["legalNameFirstName"], greenhouse: ["first_name"], lever: ["name (first part)"], ashby: ["firstName"] }, value: firstName },
    { label: "Last name", ats: { workday: ["legalNameLastName"], greenhouse: ["last_name"], lever: ["name (last part)"], ashby: ["lastName"] }, value: lastName },
    { label: "Full name", ats: { workday: ["preferredName"], greenhouse: ["full_name"], lever: ["name"], ashby: ["fullName"] }, value: fullName },
    { label: "Phone", ats: { workday: ["phone"], greenhouse: ["phone"], lever: ["phone"], ashby: ["phoneNumber"] }, value: phone },
    { label: "Pronouns", ats: { greenhouse: ["pronouns"], ashby: ["pronouns"] }, value: pronouns },
    { label: "Headline / current title", ats: { workday: ["currentJobTitle"], greenhouse: ["job_title"], lever: ["headline"], ashby: ["currentTitle"] }, value: headline || latestRole?.role || "" },
    { label: "Location", ats: { workday: ["currentLocation"], greenhouse: ["location"], lever: ["location"], ashby: ["location"] }, value: location },
    { label: "LinkedIn URL", ats: { workday: ["linkedInProfile"], greenhouse: ["linkedin_url"], lever: ["urls.LinkedIn"], ashby: ["linkedinUrl"] }, value: linkedinUrl },
    { label: "GitHub URL", ats: { workday: ["websiteUrl"], greenhouse: ["website"], lever: ["urls.GitHub"], ashby: ["githubUrl"] }, value: githubUrl },
    { label: "Portfolio URL", ats: { workday: ["portfolioUrl"], greenhouse: ["portfolio_url"], lever: ["urls.Portfolio"], ashby: ["portfolioUrl"] }, value: portfolioUrl },
    { label: "Professional summary", ats: { workday: ["summary"], greenhouse: ["cover_letter (paste as fallback)"], lever: ["summary"], ashby: ["summary"] }, value: summary, multiline: true },
    { label: "Most recent employer", ats: { workday: ["mostRecentCompany"], greenhouse: ["current_company"], lever: ["organizations[0]"], ashby: ["currentCompany"] }, value: latestRole?.company ?? "" },
    { label: "Most recent job title", ats: { workday: ["mostRecentJobTitle"], greenhouse: ["current_title"], lever: ["headline"], ashby: ["currentTitle"] }, value: latestRole?.role ?? "" },
    { label: "Top skills (comma-separated)", ats: { workday: ["skills"], greenhouse: ["skills"], lever: ["tags"], ashby: ["skills"] }, value: topSkills.join(", "), multiline: true },
    { label: "Programming languages", ats: { workday: ["technicalSkills"], greenhouse: ["custom_fields.languages"], lever: ["tags"], ashby: ["languages"] }, value: topLanguages.join(", ") },
    { label: "Work history block", ats: { workday: ["workExperience"], greenhouse: ["employments"], lever: ["positions"], ashby: ["workExperience"] }, value: workHistoryBlock, multiline: true },
    { label: "Education", ats: { workday: ["education"], greenhouse: ["educations"], lever: ["education"], ashby: ["education"] }, value: educationBlock, multiline: true },
    { label: "Work authorization", ats: { workday: ["workAuthorization"], greenhouse: ["work_authorization"], lever: ["work_authorization"], ashby: ["workAuthorization"] }, value: workAuth },
    { label: "Requires sponsorship?", ats: { workday: ["sponsorship"], greenhouse: ["requires_sponsorship"], lever: ["requires_sponsorship"], ashby: ["requiresSponsorship"] }, value: sponsorship },
    { label: "Gender", ats: { greenhouse: ["gender"], workday: ["gender"], ashby: ["gender"] }, value: gender },
    { label: "Race / Ethnicity", ats: { greenhouse: ["race"], workday: ["ethnicity"], ashby: ["race"] }, value: race },
    { label: "Hispanic / Latino?", ats: { greenhouse: ["hispanic_or_latino"], ashby: ["hispanicLatino"] }, value: hispanic },
    { label: "Veteran status", ats: { greenhouse: ["veteran_status"], workday: ["veteranStatus"], ashby: ["veteranStatus"] }, value: veteran },
    { label: "Disability status", ats: { greenhouse: ["disability_status"], workday: ["disabilityStatus"], ashby: ["disabilityStatus"] }, value: disability },
    { label: "Sexual orientation", ats: { greenhouse: ["sexual_orientation"], ashby: ["sexualOrientation"] }, value: orientation },
  ];

  const copy = async (key: string, text: string) => {
    if (!text) {
      toast.error("Nothing to copy yet.");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
      toast.success("Copied");
    } catch {
      toast.error("Clipboard blocked by browser.");
    }
  };

  const copyAllJson = async () => {
    const payload: Record<string, string> = {};
    for (const f of fields) {
      const keys = f.ats[tab] ?? [f.label];
      for (const k of keys) payload[k] = f.value;
    }
    await copy("__all__", JSON.stringify(payload, null, 2));
  };

  // Build a single paste-friendly text block for forms with a generic
  // "paste your resume" textarea. Skips empty values and EEO unless filled.
  const atsTextBlock = useMemo(() => {
    const lines: string[] = [];
    const push = (label: string, value: string) => {
      if (value && value.trim()) lines.push(`${label}: ${value.trim()}`);
    };

    if (fullName) lines.push(fullName);
    if (headline || latestRole?.role) lines.push(headline || (latestRole?.role ?? ""));
    const contact = [phone, location].filter(Boolean).join(" · ");
    if (contact) lines.push(contact);
    const links = [linkedinUrl, githubUrl, portfolioUrl].filter(Boolean).join(" · ");
    if (links) lines.push(links);

    if (summary) {
      lines.push("", "SUMMARY", summary);
    }

    if (workHistoryBlock) {
      lines.push("", "EXPERIENCE", workHistoryBlock);
    }

    if (educationBlock) {
      lines.push("", "EDUCATION", educationBlock);
    }

    const certs = profile.resume.certifications ?? [];
    if (certs.length > 0) {
      lines.push("", "CERTIFICATIONS", certs.map((c) => `• ${c}`).join("\n"));
    }

    const awards = profile.resume.awards ?? [];
    if (awards.length > 0) {
      lines.push("", "AWARDS & HONORS", awards.map((a) => `• ${a}`).join("\n"));
    }

    if (topSkills.length > 0) {
      lines.push("", "SKILLS", topSkills.join(", "));
    }

    if (topLanguages.length > 0) {
      lines.push("", "PROGRAMMING LANGUAGES", topLanguages.join(", "));
    }

    const eeoLines: string[] = [];
    if (workAuth) eeoLines.push(`Work authorization: ${workAuth}`);
    if (sponsorship) eeoLines.push(`Requires sponsorship: ${sponsorship}`);
    if (pronouns) eeoLines.push(`Pronouns: ${pronouns}`);
    if (gender) eeoLines.push(`Gender: ${gender}`);
    if (race) eeoLines.push(`Race / Ethnicity: ${race}`);
    if (hispanic) eeoLines.push(`Hispanic / Latino: ${hispanic}`);
    if (veteran) eeoLines.push(`Veteran status: ${veteran}`);
    if (disability) eeoLines.push(`Disability status: ${disability}`);
    if (orientation) eeoLines.push(`Sexual orientation: ${orientation}`);
    if (eeoLines.length > 0) {
      lines.push("", "ADDITIONAL INFORMATION", ...eeoLines);
    }

    return lines.join("\n");
  }, [
    fullName, headline, latestRole, phone, location, linkedinUrl, githubUrl,
    portfolioUrl, summary, workHistoryBlock, educationBlock,
    profile.resume.certifications, profile.resume.awards,
    topSkills, topLanguages, workAuth, sponsorship, pronouns, gender, race,
    hispanic, veteran, disability, orientation,
  ]);

  const copyAtsBlock = async () => {
    await copy("__ats_block__", atsTextBlock);
  };

  const bookmarkletProfile: BookmarkletProfile = useMemo(
    () => ({
      firstName,
      lastName,
      fullName,
      phone,
      pronouns,
      location,
      headline: headline || latestRole?.role || "",
      linkedin: linkedinUrl,
      github: githubUrl,
      portfolio: portfolioUrl,
      summary,
      currentCompany: latestRole?.company ?? "",
      currentTitle: latestRole?.role ?? "",
      skills: topSkills.join(", "),
      workAuthorization: workAuth,
      sponsorship,
      gender,
      race,
      hispanicLatino: hispanic,
      veteran,
      disability,
      sexualOrientation: orientation,
    }),
    [firstName, lastName, fullName, phone, pronouns, location, headline, latestRole, linkedinUrl, githubUrl, portfolioUrl, summary, topSkills, workAuth, sponsorship, gender, race, hispanic, veteran, disability, orientation],
  );

  const bookmarkletHref = useMemo(
    () => buildBookmarkletHref(bookmarkletProfile),
    [bookmarkletProfile],
  );

  return (
    <Box>
      {!hideHeader && (
        <PageHeader
          title="Agentic Autofill"
          subtitle="Drag the bookmarklet to your bookmarks bar — then one click fills any Workday/Greenhouse/Lever/Ashby form."
        />
      )}

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ alignItems: { sm: "center" }, justifyContent: "space-between", mb: 1.5 }}
          >
            <Box>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                <BookmarkAddedIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  One-click bookmarklet
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Drag this button to your bookmarks bar. On any application page,
                click it to autofill matching fields with your profile data.
              </Typography>
            </Box>
            {/* eslint-disable-next-line react/jsx-no-script-url */}
            <Button
              component="a"
              href={bookmarkletHref}
              variant="contained"
              size="large"
              startIcon={<BookmarkAddedIcon />}
              draggable
              onClick={(e) => {
                e.preventDefault();
                toast.info(
                  "Drag this button to your bookmarks bar. Clicking it here is a no-op for safety.",
                );
              }}
              sx={{ whiteSpace: "nowrap" }}
            >
              JobYoda Autofill
            </Button>
          </Stack>
          <Alert
            severity="info"
            sx={{ mt: 1 }}
            action={
              <Button
                component={Link}
                to="/app/profile"
                size="small"
                color="inherit"
                startIcon={<EditIcon fontSize="small" />}
              >
                Edit profile
              </Button>
            }
          >
            Works on Workday, Greenhouse, Lever, Ashby and most ATS forms. All
            values below come from your <strong>Profile</strong> — edit there
            to update them everywhere. Re-drag the bookmarklet after editing
            to refresh the data baked into it.
          </Alert>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ alignItems: { sm: "center" }, justifyContent: "space-between", mb: 2 }}
          >
            <Tabs
              value={tab}
              onChange={(_, v: AtsKey) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="workday" label="Workday" />
              <Tab value="greenhouse" label="Greenhouse" />
              <Tab value="lever" label="Lever" />
              <Tab value="ashby" label="Ashby" />
            </Tabs>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                startIcon={copied === "__ats_block__" ? <CheckIcon /> : <ArticleIcon />}
                onClick={copyAtsBlock}
                disabled={!atsTextBlock}
              >
                {copied === "__ats_block__" ? "Copied" : "Copy ATS text block"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={copied === "__all__" ? <CheckIcon /> : <DataObjectIcon />}
                onClick={copyAllJson}
              >
                {copied === "__all__" ? "Copied JSON" : "Copy all as JSON"}
              </Button>
            </Stack>
          </Stack>

          <Alert severity="info" sx={{ mb: 2 }}>
            Pick the ATS the job is using, then click the copy icon next to any
            field. Values are read-only here — to change anything, edit your
            <strong> Profile</strong>.
          </Alert>

          <Stack divider={<Divider flexItem />} spacing={1.5}>
            {fields.map((f) => {
              const key = f.label;
              const atsKeys = f.ats[tab] ?? [];
              return (
                <Box key={key} sx={{ py: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center", mb: 0.5, flexWrap: "wrap", rowGap: 0.5 }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {f.label}
                    </Typography>
                    {atsKeys.length > 0 && (
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5, minWidth: 0 }}>
                        {atsKeys.map((k) => (
                          <Chip
                            key={k}
                            size="small"
                            variant="outlined"
                            label={k}
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 11,
                              maxWidth: "100%",
                              "& .MuiChip-label": { whiteSpace: "normal", lineHeight: 1.3, py: 0.25 },
                              height: "auto",
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
                    <TextField
                      value={f.value}
                      placeholder={f.value ? "" : "Add this in your Profile to fill it here"}
                      fullWidth
                      size="small"
                      multiline={f.multiline}
                      minRows={f.multiline ? 3 : undefined}
                      maxRows={f.multiline ? 12 : undefined}
                      slotProps={{
                        input: {
                          readOnly: true,
                          sx: { fontFamily: f.multiline ? "monospace" : undefined, fontSize: 13 },
                        },
                      }}
                    />
                    <Tooltip title={copied === key ? "Copied" : "Copy"}>
                      <span>
                        <IconButton
                          onClick={() => copy(key, f.value)}
                          disabled={!f.value}
                          color={copied === key ? "success" : "default"}
                        >
                          {copied === key ? <CheckIcon /> : <ContentCopyIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
